import {
  type ActivityApprovalRequest,
  type ActivityDetail,
  ActivityDetailSchema,
  type ActivityFacets,
  ActivityFacetsSchema,
  type ActivityListQuery,
  type ActivityListResponse,
  ActivityListResponseSchema,
  type ActivityPhoto,
  type CreateActivityRequest,
  type UpdateActivityRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

export const listActivities = async (query: ActivityListQuery): Promise<ActivityListResponse> => {
  const { data } = await apiClient.get<unknown>('/activities', { params: query });
  return ActivityListResponseSchema.parse(data);
};

export const getActivity = async (id: string): Promise<ActivityDetail> => {
  const { data } = await apiClient.get<unknown>(`/activities/${id}`);
  return ActivityDetailSchema.parse(data);
};

export const createActivity = async (payload: CreateActivityRequest): Promise<ActivityDetail> => {
  const { data } = await apiClient.post<unknown>('/activities', payload);
  return ActivityDetailSchema.parse(data);
};

export const updateActivity = async (
  id: string,
  payload: UpdateActivityRequest,
): Promise<ActivityDetail> => {
  const { data } = await apiClient.patch<unknown>(`/activities/${id}`, payload);
  return ActivityDetailSchema.parse(data);
};

export const approveActivity = async (
  id: string,
  payload: ActivityApprovalRequest,
): Promise<ActivityDetail> => {
  const { data } = await apiClient.post<unknown>(`/activities/${id}/approval`, payload);
  return ActivityDetailSchema.parse(data);
};

export const deleteActivity = async (id: string): Promise<void> => {
  await apiClient.delete(`/activities/${id}`);
};

export const duplicateActivity = async (id: string): Promise<ActivityDetail> => {
  const { data } = await apiClient.post<unknown>(`/activities/${id}/duplicate`);
  return ActivityDetailSchema.parse(data);
};

export const bulkDeleteActivities = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/activities/bulk-delete', { ids });
  return data;
};

/**
 * Facets for the Country / City list filters - only the countries and cities
 * actually used by activities (not the whole geography master). Grows as
 * activities for new places are added.
 */
export const getActivityFacets = async (): Promise<ActivityFacets> => {
  const { data } = await apiClient.get<unknown>('/activities/facets');
  return ActivityFacetsSchema.parse(data);
};

/**
 * Apply the same cover + gallery images to many activities at once. There is no
 * server-side bulk-media endpoint, so this fans out a partial PATCH per id
 * (`photos` + `galleryImageMediaIds` only, everything else left unchanged) and
 * aggregates the outcome. Used by the Activities -> Media bulk-assign tool.
 */
export const bulkApplyActivityMedia = async (
  ids: string[],
  media: { photos: ActivityPhoto[] | null; galleryImageMediaIds: string[] },
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const failed: { id: string; error: string }[] = [];
  let updated = 0;
  for (const id of ids) {
    try {
      await updateActivity(id, media);
      updated += 1;
    } catch (err) {
      failed.push({ id, error: err instanceof Error ? err.message : 'Update failed' });
    }
  }
  return { updated, failed };
};
